import React, { useState } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Search, Receipt, Bell, Settings, Menu, X,
  Zap, LogOut, Package, MapPin, CreditCard, FileText, User
} from 'lucide-react';

const navItems = [
  { path: '/customer/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/customer/tracking', label: 'Track Package', icon: Search },
  { path: '/customer/invoices', label: 'Invoices', icon: Receipt },
  { path: '/customer/payments', label: 'Payments', icon: CreditCard },
  { path: '/customer/address', label: 'Address Book', icon: MapPin },
  { path: '/customer/notifications', label: 'Notifications', icon: Bell },
  { path: '/customer/settings', label: 'Settings', icon: Settings },
];

// Mock customer data
const customerData = {
  name: 'Marcus Johnson',
  email: 'marcus.johnson@email.com',
  shippingCode: 'MJ-4521',
  balance: 12450.00
};

export default function CustomerLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    navigate('/customer/login');
  };

  return (
    <div className="app-layout customer-portal" data-testid="customer-portal">
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="mobile-overlay"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`app-sidebar ${sidebarOpen ? 'open' : 'collapsed'} ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="logo-container">
            <div className="logo-icon">
              <Zap size={24} />
            </div>
            {sidebarOpen && (
              <div className="logo-text">
                <span className="logo-title">Bolt Nexus</span>
                <span className="logo-subtitle">Customer Portal</span>
              </div>
            )}
          </div>
          <button 
            className="sidebar-toggle desktop-only"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="sidebar-toggle"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section">
            <span className="nav-section-label">{sidebarOpen ? 'Menu' : ''}</span>
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`nav-${item.path.split('/').pop()}`}
              >
                <item.icon size={20} />
                {sidebarOpen && <span className="nav-label">{item.label}</span>}
              </NavLink>
            ))}
          </div>
        </nav>

        <div className="sidebar-footer">
          {sidebarOpen && (
            <>
              {/* Balance Display */}
              <div className="balance-display">
                <span className="balance-label">Outstanding Balance</span>
                <span className="balance-amount">JMD {customerData.balance.toLocaleString()}</span>
              </div>
              
              <div className="user-info">
                <div className="user-avatar">
                  {customerData.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div className="user-details">
                  <span className="user-name">{customerData.name}</span>
                  <span className="user-role">Code: {customerData.shippingCode}</span>
                </div>
              </div>
            </>
          )}
          <button 
            className="logout-btn" 
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut size={18} />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="app-main">
        <header className="app-header">
          <div className="header-left">
            <button 
              className="mobile-menu-btn mobile-only"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              <Menu size={24} />
            </button>
            <h1 className="page-title">
              {navItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
            </h1>
          </div>
          <div className="header-right">
            <div className="quick-track">
              <Search size={16} />
              <input 
                type="text" 
                placeholder="Quick track..."
                className="quick-track-input"
                data-testid="quick-track-input"
              />
            </div>
            <button className="notification-btn" data-testid="header-notifications">
              <Bell size={20} />
              <span className="notification-badge">2</span>
            </button>
          </div>
        </header>

        <div className="app-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
